import { useState, useEffect, useRef } from 'react'
import { Box, Flex, VStack, Spinner } from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormColors, FormHeader, WizardSidebar } from '@minetrace/ui/forms'
import { toaster, Text, useT } from '@minetrace/ui'
import {
  LuUser as User,
  LuShieldCheck as ShieldCheck,
  LuKeyRound as Key,
} from 'react-icons/lu'
import { personsService } from '../../api/services'
import { useRequest } from '../../hooks'
import type { PersonResponse } from '../../api/models/response'
import type { RoleResponse } from '../../api/models/response/usuario'

import Step1Data, { type Step1LookupResult } from './steps/Step1Data'
import Step2Roles from './steps/Step2Roles'
import Step3Account from './steps/Step3Account'

import type { Step1DataFormValues } from './steps/useStep1DataForm'
import type { Step2RolesFormValues } from './steps/useStep2RolesForm'
import type { Step3AccountFormValues } from './steps/useStep3AccountForm'

type StepKey = 'datos' | 'roles' | 'cuenta'

const STEP_ORDER: StepKey[] = ['datos', 'roles', 'cuenta']

/** `RecordStatus.Pending` — el alta se empezó y no se finalizó. */
const PERSON_STATUS_DRAFT = 0

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StepFormRef<T> = { trigger: () => Promise<boolean>; getValues: () => T; setValue?: any; reset?: any } | null

export default function NuevaPersonaPage() {
  const navigate = useNavigate()
  const c = useFormColors()
  const { t } = useT()
  const { id } = useParams()

  // Abrir una persona por id puede significar dos cosas distintas, y confundirlas
  // deja borradores que nunca se activan: si el registro está en estado Borrador
  // (Pending) se RETOMA el alta —y al terminar se finaliza, que es lo único que lo
  // pasa a Activo—; si ya está activa, se EDITA. `isDraft` se resuelve al cargar.
  const [isDraft, setIsDraft] = useState(false)
  const isEditing = Boolean(id) && !isDraft

  const [activeStep, setActiveStep] = useState<StepKey>('datos')
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set())
  const [loadingPerson, setLoadingPerson] = useState(Boolean(id))
  // Los tres pasos se montan a la vez (se ocultan con display:none) y cada
  // formulario fija sus defaultValues al montarse: por eso el wizard no se
  // renderiza hasta que la persona esté cargada, o el paso 1 quedaría
  // inicializado en blanco para siempre.
  const [, setExistingPerson] = useState<PersonResponse | null>(null)

  const [personId, setPersonId] = useState<string | null>(null)
  const [roles, setRoles] = useState<RoleResponse[]>([])
  const [submitting, setSubmitting] = useState(false)

  // Defaults hydrated from a pending person (resume) or the loaded edit target.
  const [step1Defaults, setStep1Defaults] = useState<Step1DataFormValues | undefined>()
  const [step2Defaults, setStep2Defaults] = useState<Step2RolesFormValues | undefined>()
  const [step3Defaults, setStep3Defaults] = useState<Step3AccountFormValues | undefined>()

  // Refs into each step's form (trigger + getValues).
  const step1Ref = useRef<StepFormRef<Step1DataFormValues>>(null)
  const step2Ref = useRef<StepFormRef<Step2RolesFormValues>>(null)
  const step3Ref = useRef<StepFormRef<Step3AccountFormValues>>(null)

  // ── API hooks ──
  const { execute: createPersonStep1 } = useRequest(personsService.createStep1)
  const { execute: updatePersonStep2 } = useRequest(
    (params: { id: string; data: Parameters<typeof personsService.updateStep2>[1] }) =>
      personsService.updateStep2(params.id, params.data),
  )
  const { execute: finalizePerson } = useRequest(
    (params: { id: string; data: Parameters<typeof personsService.finalize>[1] }) =>
      personsService.finalize(params.id, params.data),
    { successMessage: t('personas.nuevo.toasts.created') },
  )
  const { execute: updatePerson } = useRequest(
    (params: { id: string; data: Parameters<typeof personsService.update>[1] }) =>
      personsService.update(params.id, params.data),
    { successMessage: t('personas.nuevo.toasts.updated') },
  )
  // Mismo endpoint, sin toast: al retomar un borrador el paso 1 guarda en cada
  // avance, y anunciar "actualizado" en mitad del alta confundiría más que ayudar.
  const { execute: updatePersonData } = useRequest(
    (params: { id: string; data: Parameters<typeof personsService.update>[1] }) =>
      personsService.update(params.id, params.data),
  )

  // ── Load roles ──
  useEffect(() => {
    personsService.getRoles().then(setRoles).catch(() => undefined)
  }, [])

  // ── Edit mode: hydrate all step defaults from the loaded person. ──
  useEffect(() => {
    if (!id) return
    setLoadingPerson(true)
    personsService
      .getById(id)
      .then((p) => {
        setExistingPerson(p)
        // Status 0 = Borrador: el alta quedó a medias y se retoma donde estaba,
        // en vez de abrirse como una edición que nunca la activaría.
        const draft = p.status === PERSON_STATUS_DRAFT
        setIsDraft(draft)
        if (draft) {
          setPersonId(p.id)
          setCompletedSteps(new Set(p.roles.length > 0 ? ['datos', 'roles'] : ['datos']))
          setActiveStep(p.roles.length > 0 ? 'cuenta' : 'roles')
        }
        setStep1Defaults({
          documentType: p.documentType,
          identityDoc: p.identityDoc,
          names: p.names,
          paternalSurname: p.paternalSurname,
          maternalSurname: p.maternalSurname ?? '',
          gender: p.gender ?? undefined,
          email: p.email ?? '',
          phoneMobile: p.phoneMobile ?? '',
          address: p.address ?? '',
        })
        setStep2Defaults({
          roleIds: p.roles.map((r) => r.roleId),
          licenseNumber: p.driverData?.licenseNumber ?? '',
          licenseCategory: p.driverData?.licenseCategory ?? '',
          licenseExpiryDate: p.driverData?.licenseExpiryDate?.split('T')[0] ?? '',
          colegiatureCode: p.geologistData?.colegiatureCode ?? '',
          colegiatureExpiryDate: p.geologistData?.colegiatureExpiryDate?.split('T')[0] ?? '',
        })
        setStep3Defaults({
          enableUserAccount: p.userAccount?.accountStatus === 2,
          username: p.userAccount?.username ?? '',
          password: '',
          isExternal: p.userAccount?.isExternal ?? false,
          hasWebAccess: p.userAccount?.hasWebAccess ?? true,
        })
      })
      .catch(() => undefined)
      .finally(() => setLoadingPerson(false))
  }, [id])

  // ── Step navigation guard. ──
  const handleStepClick = (next: StepKey) => {
    if (isEditing) {
      setActiveStep(next)
      return
    }
    const nextIdx = STEP_ORDER.indexOf(next)
    const requiredPrev = STEP_ORDER.slice(0, nextIdx)
    const allDone = requiredPrev.every((k) => completedSteps.has(k))
    if (allDone) setActiveStep(next)
  }

  // ── Step 1 (datos): Lookup resolution from the SUNAT button.
  // When the doc is found in the local DB we just navigate to that person's
  // edit page; SUNAT autofills the name fields in-place via the step itself.
  const onStep1Lookup = (lookup: Step1LookupResult) => {
    if (lookup.existingPersonId) {
      navigate(`/app/personas/${lookup.existingPersonId}`)
    }
  }

  // ── Step submission handlers ──
  const onSubmitStep1 = async () => {
    if (!step1Ref.current) return
    const ok = await step1Ref.current.trigger()
    if (!ok) return
    const data = step1Ref.current.getValues()
    if (data.documentType === 1 && !/^\d{8}$/.test(data.identityDoc.trim())) {
      toaster.error({ description: t('personas.nuevo.errors.dniInvalid') })
      return
    }

    if (personId) {
      // Se está retomando un borrador. Antes se avanzaba sin más, y todo lo que el
      // operador hubiera corregido en este paso —el documento incluido— se perdía en
      // silencio: el wizard terminaba finalizando la persona del borrador, no la que
      // se acababa de escribir. Se guardan los cambios sobre ese mismo registro.
      setSubmitting(true)
      try {
        const updated = await updatePersonData({
          id: personId,
          data: {
            id: personId,
            documentType: data.documentType,
            identityDoc: data.identityDoc.trim(),
            names: data.names,
            paternalSurname: data.paternalSurname,
            maternalSurname: data.maternalSurname || undefined,
            gender: data.gender || undefined,
            email: data.email,
            phoneMobile: data.phoneMobile,
            address: data.address || undefined,
          },
        })
        if (!updated) return
        setCompletedSteps((prev) => new Set([...prev, 'datos']))
        setActiveStep('roles')
      } finally {
        setSubmitting(false)
      }
      return
    }

    setSubmitting(true)
    try {
      const result = await createPersonStep1({
        documentType: data.documentType,
        identityDoc: data.identityDoc.trim(),
        names: data.names,
        paternalSurname: data.paternalSurname,
        maternalSurname: data.maternalSurname || undefined,
        gender: data.gender || undefined,
        email: data.email,
        phoneMobile: data.phoneMobile,
        address: data.address || undefined,
      })
      if (!result) return
      setPersonId(result.id)
      setCompletedSteps((prev) => new Set([...prev, 'datos']))
      setActiveStep('roles')
    } finally {
      setSubmitting(false)
    }
  }

  const onSubmitStep2 = async () => {
    if (!step2Ref.current || !personId) return
    const ok = await step2Ref.current.trigger()
    if (!ok) return
    const data = step2Ref.current.getValues()
    const TRANSPORTISTA = 'd4e5f6a7-b8c9-0123-defa-234567890123'
    const GEOLOGO = 'f6a7b8c9-d0e1-2345-fabc-456789012345'

    setSubmitting(true)
    try {
      const result = await updatePersonStep2({
        id: personId,
        data: {
          roleIds: data.roleIds,
          driverData: data.roleIds.includes(TRANSPORTISTA)
            ? {
                licenseNumber: data.licenseNumber || undefined,
                licenseCategory: data.licenseCategory || undefined,
                licenseExpiryDate: data.licenseExpiryDate || undefined,
              }
            : undefined,
          geologistData: data.roleIds.includes(GEOLOGO)
            ? {
                colegiatureCode: data.colegiatureCode || undefined,
                colegiatureExpiryDate: data.colegiatureExpiryDate || undefined,
              }
            : undefined,
        },
      })
      if (!result) return
      setCompletedSteps((prev) => new Set([...prev, 'roles']))
      setActiveStep('cuenta')
    } finally {
      setSubmitting(false)
    }
  }

  const onSubmitStep3 = async () => {
    if (!step3Ref.current || !personId) return
    const ok = await step3Ref.current.trigger()
    if (!ok) return
    const data = step3Ref.current.getValues()

    setSubmitting(true)
    try {
      const result = await finalizePerson({
        id: personId,
        data: {
          enableUserAccount: data.enableUserAccount,
          username: data.username || undefined,
          password: data.password || undefined,
          isExternal: data.isExternal,
          hasWebAccess: data.hasWebAccess,
        },
      })
      if (!result) return
      setTimeout(() => navigate('/app/personas'), 1500)
    } finally {
      setSubmitting(false)
    }
  }

  const onSubmitEdit = async () => {
    if (!id) return
    if (!step1Ref.current || !step2Ref.current || !step3Ref.current) return
    const oks = await Promise.all([
      step1Ref.current.trigger(),
      step2Ref.current.trigger(),
      step3Ref.current.trigger(),
    ])
    if (oks.some((ok) => !ok)) {
      toaster.error({ description: 'Hay campos sin completar.' })
      return
    }
    const data = step1Ref.current.getValues()
    const rolesData = step2Ref.current.getValues()
    const accountData = step3Ref.current.getValues()
    const TRANSPORTISTA = 'd4e5f6a7-b8c9-0123-defa-234567890123'
    const GEOLOGO = 'f6a7b8c9-d0e1-2345-fabc-456789012345'

    setSubmitting(true)
    try {
      const result = await updatePerson({
        id,
        data: {
          documentType: data.documentType,
          identityDoc: data.identityDoc.trim(),
          names: data.names,
          paternalSurname: data.paternalSurname,
          maternalSurname: data.maternalSurname || undefined,
          gender: data.gender || undefined,
          email: data.email,
          phoneMobile: data.phoneMobile,
          address: data.address || undefined,
          roleIds: rolesData.roleIds,
          driverData: rolesData.roleIds.includes(TRANSPORTISTA)
            ? {
                licenseNumber: rolesData.licenseNumber || undefined,
                licenseCategory: rolesData.licenseCategory || undefined,
                licenseExpiryDate: rolesData.licenseExpiryDate || undefined,
              }
            : undefined,
          geologistData: rolesData.roleIds.includes(GEOLOGO)
            ? {
                colegiatureCode: rolesData.colegiatureCode || undefined,
                colegiatureExpiryDate: rolesData.colegiatureExpiryDate || undefined,
              }
            : undefined,
          enableUserAccount: accountData.enableUserAccount,
          username: accountData.username || undefined,
          newPassword: accountData.password || undefined,
          isExternal: accountData.isExternal,
          hasWebAccess: accountData.hasWebAccess,
        },
      })
      if (!result) return
      setTimeout(() => navigate('/app/personas'), 1500)
    } finally {
      setSubmitting(false)
    }
  }

  // ── Loading state ──
  if (loadingPerson) {
    return (
      <VStack gap={6} alignItems="stretch">
        <FormHeader
          title={t('personas.nuevo.loadingTitle')}
          subtitle={t('personas.nuevo.loadingSubtitle')}
          backPath="/app/personas"
        />
        <Box p={8} textAlign="center">
          <Spinner color={c.accent} />
        </Box>
      </VStack>
    )
  }

  const sidebarSteps = [
    { key: 'datos', label: 'Datos personales', icon: <User size={18} /> },
    { key: 'roles', label: 'Roles', icon: <ShieldCheck size={18} /> },
    { key: 'cuenta', label: 'Cuenta de usuario', icon: <Key size={18} /> },
  ]

  const stepIdx = STEP_ORDER.indexOf(activeStep)

  // Submit dispatcher
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isEditing) return onSubmitEdit()
    if (activeStep === 'datos') return onSubmitStep1()
    if (activeStep === 'roles') return onSubmitStep2()
    if (activeStep === 'cuenta') return onSubmitStep3()
  }

  const submitLabel = (() => {
    if (isEditing) return 'Guardar cambios'
    if (activeStep === 'cuenta') return 'Finalizar registro'
    return 'Continuar'
  })()

  const stepHeader = (() => {
    switch (activeStep) {
      case 'datos':
        return { title: 'Datos personales', description: 'Tipo y número de documento, nombres y datos de contacto. En DNI puede consultar SUNAT para autocompletar.' }
      case 'roles':
        return { title: 'Roles', description: 'Asigna uno o más roles. Si la persona es transportista o geólogo, llena los campos específicos.' }
      case 'cuenta':
        return { title: 'Cuenta de usuario', description: 'Define si la persona es interna o externa, si tiene cuenta y si puede acceder a la web.' }
    }
  })()

  return (
    <Flex mx={-8} mt={-4}>
      <WizardSidebar
        title={isEditing ? 'Editar persona' : isDraft ? 'Continuar registro' : 'Nueva persona'}
        subtitle={`Paso ${stepIdx + 1} de ${STEP_ORDER.length}`}
        steps={sidebarSteps}
        activeStep={activeStep}
        completedSteps={completedSteps}
        onStepClick={(next) => handleStepClick(next as StepKey)}
      />

      <Box flex={1} py={4} px={6} bg="white" display="flex" flexDirection="column" minH="calc(100vh - 2rem)">
        <Box mb={6}>
          <Text fontSize="3xl" fontWeight="800" color={c.accent} fontFamily="heading">
            {stepHeader.title}
          </Text>
          <Text color={c.textSecondary} mt={2} maxW="2xl">
            {stepHeader.description}
          </Text>
        </Box>

        <form onSubmit={handleSubmit} noValidate style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box flex={1}>
            {/* All three steps stay mounted — switching tabs only toggles
                visibility. This keeps each step's form state alive (so values
                typed in step 1 persist if the user jumps to step 2 and back). */}
            <Box display={activeStep === 'datos' ? 'block' : 'none'}>
              <Step1Data
                ref={step1Ref}
                defaultValues={step1Defaults}
                isEditing={isEditing}
                onLookupResolved={onStep1Lookup}
              />
            </Box>
            <Box display={activeStep === 'roles' ? 'block' : 'none'}>
              <Step2Roles ref={step2Ref} defaultValues={step2Defaults} roles={roles} />
            </Box>
            <Box display={activeStep === 'cuenta' ? 'block' : 'none'}>
              <Step3Account ref={step3Ref} defaultValues={step3Defaults} isEditing={isEditing} />
            </Box>
          </Box>

          <Flex justifyContent="space-between" mt={6} pt={4} borderTop="1px solid" borderColor={c.border}>
            <Box
              as="button"
              type="button"
              px={4}
              py={2}
              fontSize="sm"
              color={c.textSecondary}
              borderRadius="lg"
              border="1px solid"
              borderColor={c.border}
              cursor="pointer"
              onClick={() => navigate('/app/personas')}
              _hover={{ bg: c.bgHover }}
            >
              Cancelar
            </Box>
            <Flex gap={2}>
              {stepIdx > 0 && !isEditing && (
                <Box
                  as="button"
                  type="button"
                  px={4}
                  py={2}
                  fontSize="sm"
                  color={c.textSecondary}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={c.border}
                  cursor="pointer"
                  onClick={() => setActiveStep(STEP_ORDER[stepIdx - 1])}
                  _hover={{ bg: c.bgHover }}
                >
                  Atrás
                </Box>
              )}
              <Box
                as="button"
                type="submit"
                px={6}
                py={2}
                fontSize="sm"
                fontWeight="700"
                color="white"
                bg={c.accent}
                borderRadius="lg"
                cursor={submitting ? 'not-allowed' : 'pointer'}
                opacity={submitting ? 0.6 : 1}
                disabled={submitting}
                display="flex"
                alignItems="center"
                gap={2}
              >
                {submitting && <Spinner size="sm" />}
                {submitLabel}
              </Box>
            </Flex>
          </Flex>
        </form>
      </Box>
    </Flex>
  )
}
